import { Component, ElementRef, inject, ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { ResourceInput } from "@fullcalendar/angular-scheduler";
import { TreeNode } from "@openng/optimus-ui/api";
import { Tree } from '@openng/optimus-ui/tree';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { GuiUtils } from "src/modules/utils/misc/gui-utils";
import { TreeSelectionHelper } from 'src/modules/utils/misc/tree-selection-helper';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from 'src/modules/utils/shared/objects';
import { Strings } from 'src/modules/utils/shared/strings';
import { Utils } from 'src/modules/utils/shared/utils';
import { FullCalendarWrapper } from '../../models/full-calendar-wrapper.model';
import { CalendarEvent } from '../../netgen/CalendarEvent';
import { CalendarEventKind } from '../../netgen/CalendarEventKind';
import { CalendarRole } from '../../netgen/CalendarRole';
import { Resource } from '../../netgen/Resource';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { ChangeSummary } from 'src/modules/enaio/netgen/ChangeSummary';

@Component({
    selector: 'app-planning-view-company',
    templateUrl: './planning-view-company.component.html',
    styleUrl: './planning-view-company.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlanningViewCompanyComponent extends ComponentView {

    service = inject(ProjectPlanningService);

    calendarWrapper: FullCalendarWrapper;
    resources: ResourceInput[] = [{ id: "", title: "", children: [] }];

    treeData: TreeSelectionHelper;
    form: FrontendForm;
    displayedCalendarIds: number[] = [];
    events: CalendarEvent[] = [];
    wasOpened = false;

    @ViewChild("fc") 
    fc: FullCalendarComponent;
    @ViewChild("treeElement") 
    tree: Tree;
    @ViewChild("treeElement", { read: ElementRef }) 
    treeElement: ElementRef;

    async initParams(): Promise<boolean> {
        this.calendarWrapper = new FullCalendarWrapper(true, false, "calendar", "type", this.calendarsChanged.bind(this), this, this.app, this.service);

        let prefSelectedNodeIds = Strings.split(localStorage.getItem("u" + this.service.getCurrentUser().id + "_companySelectedCalendarIds") ?? "", ",").map(id => Utils.toNumber(id));

        this.treeData = new TreeSelectionHelper(this.tree, false);
        await this.updateTree();
        let userId = this.service.getCurrentUser().id;
        let responsibleForRoles = (await this.service.getRoles()).filter(r => r.responsibleId == userId);
        this.treeData.setSelected(prefSelectedNodeIds.length > 0 ? 
            prefSelectedNodeIds : 
            ["21" + userId, ...responsibleForRoles.map(r => "22" + r.id)]);
        if (prefSelectedNodeIds.length == 0 && !Utils.isNoe(responsibleForRoles)) {
            this.treeData.getNode("02").expanded = true;
        }       

        this.form = new FrontendForm([
            new FrontendField("statusActive", this.app.t("ProjectPlanning.active"), FrontendFieldType.bool, { 
                value: true,
                onValueChanged: this.initFullCalendar.bind(this) 
            }),
            new FrontendField("statusFinished", this.app.t("ProjectPlanning.finished"), FrontendFieldType.bool, {
                onValueChanged: this.initFullCalendar.bind(this) 
            }),          
            new FrontendField("splitter1", "", FrontendFieldType.splitter),
            new FrontendField("typeEvent", this.app.t("entity.CalendarEvent.other.appointmentPlural"), FrontendFieldType.bool, { 
                value: true,
                onValueChanged: this.initFullCalendar.bind(this) 
            }),
            new FrontendField("typeTask", this.app.t("entity.CalendarEvent.other.taskPlural"), FrontendFieldType.bool, { 
                value: true,
                onValueChanged: this.initFullCalendar.bind(this) 
            }),
            new FrontendField("typeMilestone", this.app.t("ProjectPlanning.milestones"), FrontendFieldType.bool, {
                value: true,
                onValueChanged: this.initFullCalendar.bind(this)
            })
        ]);
        this.updateIsMobile();

        this.addSubscription(this.app.registerChangesCallback(this.serviceChanged.bind(this), "PlanningViewCompany"));
        this.addSubscription(this.app.onIsMobileChanged.subscribe(() => this.updateIsMobile()));

        return true;
    }

    updateIsMobile(): void {
        this.form.getField("statusActive").guiGroup = this.app.isMobile() ? "1" : null;
        this.form.getField("statusFinished").guiGroup = this.app.isMobile() ? "1" : null;
        this.form.getField("statusActive").guiGroupInline = this.app.isMobile();
        this.form.getField("statusFinished").guiGroupInline = this.app.isMobile();
        this.form.getField("typeEvent").guiGroup = this.app.isMobile() ? "2" : null;
        this.form.getField("typeTask").guiGroup = this.app.isMobile() ? "2" : null;
        this.form.getField("typeMilestone").guiGroup = this.app.isMobile() ? "2" : null;
        this.form.getField("typeEvent").guiGroupInline = this.app.isMobile();
        this.form.getField("typeTask").guiGroupInline = this.app.isMobile();
        this.form.getField("typeMilestone").guiGroupInline = this.app.isMobile();
        this.form.getField("splitter1").visible = !this.app.isMobile();

        this.form = new FrontendForm(this.form.fields);
    }

    async setWasOpened(): Promise<void> {
        this.wasOpened = true;
        await this.calendarsChanged();
    }

    async showEvent(event: CalendarEvent): Promise<void> {
        this.wasOpened = true;
        let calendarIds = new Set(event.instances?.map(instance => instance.calendarId) ?? []);
        let calendarNodes = GuiUtils.flattenTree(this.treeData.items).filter(node => calendarIds.has(node.data?.calendarId as number));
        if (calendarNodes.length == 0) {
            await this.app.dialog.info(this.app.t("ProjectPlanning.globalSearchNoSelectableCalendar", { eventId: event.id }), this.app.t("ProjectPlanning.globalSearch"));
            return;
        }

        let calendarNodeKeys = calendarNodes.map(node => node.key).filter(key => key != null);
        this.treeData.setSelected(Arrays.getUnique([...this.treeData.getSelected(), ...calendarNodeKeys]));
        this.expandTreePaths(this.treeData.items, new Set(calendarNodeKeys));
        this.form.setValue(event.finished ? "statusFinished" : "statusActive", true);
        this.form.setValue(event.kind == CalendarEventKind.appointment ? "typeEvent" : event.kind == CalendarEventKind.task ? "typeTask" : "typeMilestone", true);
        await this.calendarsChanged();
        this.calendarWrapper.focusEvent(event);
    }

    private expandTreePaths(nodes: TreeNode[], targetKeys: Set<string>): boolean {
        let containsTarget = false;
        for (let node of nodes) {
            let childContainsTarget = this.expandTreePaths(node.children ?? [], targetKeys);
            if (childContainsTarget) {
                node.expanded = true;
            }
            containsTarget ||= node.key != null && targetKeys.has(node.key) || childContainsTarget;
        }
        return containsTarget;
    }

    async serviceChanged(summary: ChangeSummary): Promise<void> {
        if (summary.hasAnyType(["CalendarRole", "ResourceType", "DatabaseUser", "DatabaseUserGroup", "Company"])) {
            await this.updateTree();
            GuiUtils.updateTreeSelectedColors(this.treeData.items, this.treeData.getSelectedNodes(), this.treeElement?.nativeElement as HTMLElement);
        }   
        if (summary.hasAnyType(["CalendarEvent", "CalendarEventRole"])) {
            await this.calendarsChanged();
        }           
    }

    async updateTree(): Promise<void> {
        this.treeData.setItems(await this.service.getGuiTree([]), true);
    }

    debouncedCalendarsChanged = Utils.debounce(async () => this.calendarsChanged(), 50);

    selectedCalendarsChanged(): void {
        this.debouncedCalendarsChanged();
    }

    async calendarsChanged(): Promise<void> {
        if (!this.wasOpened) {
            return;
        }

        this.displayedCalendarIds = Arrays.withoutNull(this.treeData.getSelectedNodes().map(sel => sel.data?.calendarId as number));
        this.events = await this.service.getEventsForCalendars(this.displayedCalendarIds);

        localStorage.setItem("u" + this.service.getCurrentUser().id + "_companySelectedCalendarIds", this.treeData.getSelected().filter(key => key.startsWith("2")).join(","));

        GuiUtils.updateTreeSelectedColors(this.treeData.items, this.treeData.getSelectedNodes(), this.treeElement?.nativeElement as HTMLElement);
        await this.initCalendar();
    }

    async initCalendar(): Promise<void> {
        let rThisUser: ResourceInput;
        let rUsers: ResourceInput = { id: "groupUsers", title: this.app.t("ProjectPlanning.employees"), children: [], priority: "1" };
        let rRoles: ResourceInput = { id: "groupRoles", title: this.app.t("entity.CalendarEvent.fields.roles"), children: [], priority: "2" };
        let rResources: ResourceInput = { id: "groupResources", title: this.app.t("entity.CalendarEvent.fields.resources"), children: [], priority: "3" };
        for (let sel of this.treeData.getSelectedNodes()) {
            if (sel.data instanceof DatabaseUser) {
                if (sel.data.id == this.service.getCurrentUser().id) {
                    rThisUser = { id: Objects.toString(sel.data.calendarId), title: sel.data.getFullName(), children: [], priority: "0" };
                }
                else {
                    let group = Arrays.getSafe(sel.data.groups, 0);
                    let companyKey = sel.data.company?.id ?? "none";
                    let groupKey = group?.id ?? "none";
                    let useUserGroupLevel = sel.data.company?.useUserGroupLevel ?? true;
                    let ri: ResourceInput = { id: Objects.toString(sel.data.calendarId), title: sel.data.getFullName(), children: [] };
                    let parent: ResourceInput = rUsers;
                    if (this.app.mainConfig.useAdminCompanies) {
                        let ricCompany = rUsers.children.find(r => r.id == "groupUsersCompany" + companyKey);
                        if (ricCompany == null) {
                            ricCompany = { id: "groupUsersCompany" + companyKey, title: sel.data.company?.name ?? this.app.t("ProjectPlanning.noCompany"), children: [], priority: sel.data.company?.getSortName() ?? "", extendedProps: { isGroup: true } };
                            rUsers.children.push(ricCompany);
                        }
                        parent = ricCompany;
                    }
                    if (useUserGroupLevel) {
                        let ric = parent.children.find(r => r.id == "groupUsers" + companyKey + "_" + groupKey);
                        if (ric == null) {
                            ric = { id: "groupUsers" + companyKey + "_" + groupKey, title: group?.name ?? this.app.t("ProjectPlanning.noUserGroup"), children: [], priority: group?.getSortName() ?? "", extendedProps: { isGroup: true} };
                            parent.children.push(ric);
                        }
                        ric.children.push(ri);
                    }
                    else {
                        parent.children.push(ri);
                    }
                }
            }
            else if (sel.data instanceof CalendarRole) {
                let ri: ResourceInput = { id: Objects.toString(sel.data.calendarId), title: sel.data.title, children: [] };
                rRoles.children.push(ri);
            }
            else if (sel.data instanceof Resource) {
                let ri: ResourceInput = { id: Objects.toString(sel.data.calendarId), title: sel.data.title, children: [] };
                let ric = rResources.children.find(r => r.id == "groupResources" + sel.data.typeId);
                if (ric == null) {
                    ric = { id: "groupResources" + sel.data.typeId, title: this.service.getResourceType(sel.data.typeId)?.title ?? "<???>", children: [], extendedProps: { isGroup: true} };
                    rResources.children.push(ric);
                }
                ric.children.push(ri);
            }
            else if (sel.data?.calendarId != null) {
                let ri: ResourceInput = { id: Objects.toString(sel.data.calendarId as number), title: sel.data.title as string, children: [] };
                this.resources.push(ri);
            }
        }
        this.resources = [rThisUser, rUsers, rRoles, rResources].filter(ri => ri != null && (ri == rThisUser || ri.children.length > 0));        

        await this.initFullCalendar();  
    }    

    private async initFullCalendar(): Promise<void> {
        if (this.form == null) {
            return;
        }
        let typeEvent = this.form.getValueBool("typeEvent");
        let typeTask = this.form.getValueBool("typeTask");
        let typeMilestone = this.form.getValueBool("typeMilestone");
        let statusActive = this.form.getValueBool("statusActive");
        let statusFinished = this.form.getValueBool("statusFinished");
        let filteredEvents = this.events.filter(e => 
            (typeEvent && e.kind == CalendarEventKind.appointment ||
            typeTask && e.kind == CalendarEventKind.task ||
            typeMilestone && e.kind == CalendarEventKind.milestone) &&
            (statusActive && !e.isFinished() ||
                    statusFinished && e.isFinished()
        )
        );
        await this.calendarWrapper.initCalendar(() => this.fc, filteredEvents, this.resources, this.displayedCalendarIds);  
    }    
}
